import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import useAutoSaveForm from 'hooks/settings/useAutoSaveForm';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidPassword } from 'utils/password';
import { CHANGE_PASSWORD_FIELD } from 'utils/settings';

const EMPTY_VALUES = Object.freeze({ current: '', new: '', confirmNew: '' });

const useChangePasswordForm = ({ onDirtyChange, onSaved } = {}) => {
    const { auth, updatePassword } = useAuth();
    const { message } = useSnackbar();
    const { t } = useTranslation();

    const getErrorMessage = useCallback(
        (key, value, values) => {
            if (key === CHANGE_PASSWORD_FIELD.CURRENT) {
                return value ? '' : t('account-settings.password.enter-password', 'Please enter your password');
            }
            if (key === CHANGE_PASSWORD_FIELD.NEW) {
                return isValidPassword(value)
                    ? ''
                    : t('account-settings.password.satisfy-requirements', 'Satisfy the following requirements:');
            }
            if (key === CHANGE_PASSWORD_FIELD.CONFIRM_NEW) {
                return value === values.new
                    ? ''
                    : t('account-settings.password.password-mismatch', 'This does not match your new password');
            }
            return '';
        },
        [t]
    );

    const formSettings = useAutoSaveForm({
        fields: CHANGE_PASSWORD_FIELD,
        getErrorMessage,
        initialValues: EMPTY_VALUES,
        persist: async ({ current, new: next }) => {
            const saved = await updatePassword(current, next);
            if (!saved) throw new Error('CURRENT_PASSWORD_INCORRECT');
        },
        onSuccess: () => {
            message.success(t('message.password-updated', 'Your password has been updated.'));
            formSettings.reset(EMPTY_VALUES);
            onSaved?.();
        },
        onError: (error) => {
            const isIncorrect = error?.message === 'CURRENT_PASSWORD_INCORRECT';
            message.error(
                t(
                    isIncorrect
                        ? 'account-settings.password.current-password-incorrect'
                        : 'message.something-went-wrong',
                    isIncorrect ? 'Your current password is incorrect.' : 'Something went wrong.'
                )
            );
        },
    });
    const { reset } = formSettings;

    useEffect(() => onDirtyChange?.(formSettings.isDirty), [formSettings.isDirty, onDirtyChange]);
    useEffect(() => {
        if (auth?.is_third_party) reset(EMPTY_VALUES);
    }, [auth?.is_third_party, reset]);

    return formSettings;
};

export default useChangePasswordForm;
